import React, { useState } from 'react';
import { useStore } from 'effector-react';
import { toFlag, toPercent } from 'pano-utils';
import {
  ImageType,
  MediaType,
  type Video,
  VideoLoopType,
  VideoPosType,
  type Voice,
  VoiceLoopType,
} from '@types';
import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import { $sceneMapApi } from '@stores/dataStores';
import { $loadedScene } from '@stores/loadedStore';
import ImageContainer from '@components/ImageContainer';
import Image from '@components/Image';
import TextLink from '@components/TextLink';
import Tip from '@components/Tip';
import Button from '@components/Button';
import Slider from '@components/Slider';
import useToggle from '@hooks/useToggle';
import getVideoDetail from '@utils/getVideoDetail';
import addComponent from '@utils/addComponent';
import VideoCaptureModal from '@components/VideoCaptureModal';
import { getMediaUrl } from '@utils/urlMakers';
import uploadVideoCover from '@utils/uploadVideoCover';
import AudioPlayer from '@components/AudioPlayer';
import { subtitleFormat } from '@utils/subtitleFormat';
import SidebarApplyTo from '@modules/sidebar/components/SidebarApplyTo';
import { sendSuccess } from '@components/Noti';
import { showSceneModal } from '@modules/category';
import FeatureToggle from '@components/FeatureToggle';
import useTemplateFeature from '@hooks/useTemplateFeature';
import { selectLibAudios, selectLibVideos } from '../../mediaLib';

type VoiceType = 'video' | 'audio';
const typeItems: DropdownItem<VoiceType>[] = [
  { id: 'video', name: 'AI视频讲解', showVip: true },
  { id: 'audio', name: 'AI音频讲解' },
];

const posItems: DropdownItem<VideoPosType>[] = [
  { id: VideoPosType.bottomRight, name: '右下' },
  { id: VideoPosType.bottomLeft, name: '左下' },
  { id: VideoPosType.bottomCenter, name: '底部居中' },
];

const audioDefault: Voice = {
  volume: 1,
  autoPlay: 1,
  isLoop: VoiceLoopType.switch,
  playEndDelay: 0,
  stopAfterPlayOnce: 0,
};

const videoDefault: Video = {
  mediaId: 0,
  mediaUrl: '',
  mediaName: '',
  mediaDuration: 0,
  videoThumbUrl: '', // 视频封面
  partnerKey: 'v',
  maskColor: '', // 抠图色值
  maskSimilarity: 0.11, // 抠图相似度
  subtitle: '', // 字幕内容
  pcPosition: VideoPosType.bottomRight,
  mobilePosition: VideoPosType.bottomRight,
  playSetting: VideoLoopType.switch,
  playEndDelay: 0,
  scale: 1,
  stopAfterPlayOnce: 0,
};

const TIP =
  '注：开启后讲解结束后隐藏视频和字幕不再讲解但是导览继续，直到刷新页面重新加载后才会讲解';

export function VoiceSetting() {
  const scene = useStore($loadedScene)!;
  const { id, voice, video: videoList = [] } = scene;
  const video = videoList[0] ?? {};
  const videoLoading = useToggle(false);

  const featureVideo = useTemplateFeature('autoRotateVideo');

  const [voiceType, setVoiceType] = useState<VoiceType>(
    featureVideo && !voice.mediaUrl ? 'video' : 'audio'
  );

  const handleTypeChange = (type: VoiceType) => {
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.voice = {};
        draft.video = [];
      },
    });
    setVoiceType(type);
  };

  const handleSelectAudio = async () => {
    const { libItems, sysItems } = await selectLibAudios();
    if (libItems) {
      const item = libItems[0];
      $sceneMapApi.updateScene({
        id,
        updater: (draft) => {
          draft.voice = {
            ...audioDefault,
            ...draft.voice,
            type: MediaType.lib,
            mediaId: item.id,
            mediaUrl: item.url,
            mediaName: item.name,
          };
          draft.video = [];
          if (item.subtitle) {
            draft.config.autoRotate!.subtitle = subtitleFormat(item.subtitle);
            draft.config.autoRotate!.subtitleFileName = `${item.name}.srt`;
          }
        },
      });
    } else if (sysItems) {
      const item = sysItems[0];
      $sceneMapApi.updateScene({
        id,
        updater: (draft) => {
          draft.voice = {
            ...audioDefault,
            ...draft.voice,
            type: MediaType.system,
            mediaId: item.id,
            mediaUrl: item.url,
            mediaName: item.name,
          };
        },
      });
    }
  };

  const handleSelectVideo = async () => {
    const { libItems } = await selectLibVideos();
    if (!libItems) return;
    videoLoading.on();
    const item = libItems[0];
    const { coverUrl, duration } = await getVideoDetail(item.url);
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.video ??= [];
        draft.video[0] ??= {};
        draft.video[0] = {
          ...videoDefault,
          ...draft.video[0],
          mediaId: item.id,
          mediaUrl: item.url,
          mediaName: item.name,
          subtitle: item.subtitle ?? '',
          videoThumbUrl: coverUrl,
          mediaDuration: duration,
          maskColor: '',
          maskSimilarity: 0.11,
          scale: 1,
        };
      },
    });
    videoLoading.off();
  };

  const handleVideoDelete = () => {
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.video = [];
      },
    });
  };

  const updateVoice = <T extends keyof Voice>({
    key,
    value,
  }: {
    key: T;
    value: Voice[T];
  }) => {
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.voice[key] = value;
      },
    });
  };

  const updateVideo = <T extends keyof Video>({
    key,
    value,
  }: {
    key: T;
    value: Video[T];
  }) => {
    $sceneMapApi.updateScene({
      id,
      updater: (draft) => {
        draft.video ??= [];
        draft.video[0] ??= {};
        draft.video[0][key] = value;
      },
    });
  };

  const handleApplyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要应用到的场景',
      subTitle: '注：只能应用到同类型场景中',
      allowedTypes: [scene.type],
      multiple: true,
    });
    if (!idSet) return;
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        draft.get(_id)!.video = videoList;
        draft.get(_id)!.voice = voice;
      });
    });
    sendSuccess('AI讲解已应用到所选场景');
  };

  const handleCoverSetting = () => {
    addComponent((remove) => (
      <VideoCaptureModal
        coverSrc={video.videoThumbUrl}
        videoUrl={getMediaUrl(video.mediaUrl)}
        onRequestClose={remove}
        title="视频封面图"
        onConfirm={async (file) => {
          const url = await uploadVideoCover(file);
          updateVideo({ key: 'videoThumbUrl', value: url });
        }}
      />
    ));
  };

  const renderAudioSetting = () => {
    return (
      <>
        <AudioPlayer
          mediaType={voice.type}
          src={voice.mediaUrl}
          name={voice.mediaName}
          volume={voice.volume}
          onDelete={() => {
            $sceneMapApi.updateScene({
              id,
              updater: (draft) => {
                draft.voice = {};
              },
            });
          }}
        />
        <div className="mt-10 flex items-center justify-between">
          <Tip>文件体积不超过10MB</Tip>
          <Button size="sm" onClick={handleSelectAudio}>
            选择音频
          </Button>
        </div>
        {!!voice.mediaUrl && (
          <>
            <Slider
              min={0}
              max={1}
              value={voice.volume}
              className="mt-20"
              valToString={(val) => toPercent(val, 0)}
              onChange={(value) => updateVoice({ key: 'volume', value })}
            >
              <Slider.Header
                title={
                  <div>
                    <span>音量：</span>
                    <Tip as="span">仅PC和安卓有效</Tip>
                  </div>
                }
              />
              <Slider.Bar step={0.01} />
            </Slider>
            <FeatureToggle
              title="讲解一遍结束后不再讲解"
              isOn={!!voice.stopAfterPlayOnce}
              onToggle={(bool) =>
                updateVoice({ key: 'stopAfterPlayOnce', value: toFlag(bool) })
              }
            />
            <Tip className="mt-10 text-orange">{TIP}</Tip>
          </>
        )}
      </>
    );
  };

  const renderVideoSetting = () => {
    return (
      <>
        <ImageContainer
          className="h-70"
          placeholder={
            <div className="flex flex-col items-center">
              {videoLoading.isOn ? (
                <div className="text-white">视频封面正在生成中…</div>
              ) : (
                <>
                  <p>视频格式：MP4</p>
                  <p>编码方式：H.264</p>
                </>
              )}
            </div>
          }
        >
          {!!video.mediaUrl && (
            <>
              <Image
                className="h-50 w-50"
                size={50}
                src={video.videoThumbUrl!}
                type={ImageType.media}
              />
              <div className="ml-10 flex flex-col items-start">
                <div className="mb-5 w-140 truncate text-white">
                  {video.mediaName}
                </div>
                <div className="flex items-center">
                  <TextLink onClick={handleCoverSetting}>设置封面</TextLink>
                  <TextLink className="ml-15" onClick={handleVideoDelete}>
                    删除
                  </TextLink>
                </div>
              </div>
            </>
          )}
        </ImageContainer>
        <div className="mt-10 flex items-center justify-between">
          <Tip>文件体积不超过10MB</Tip>
          <Button size="sm" onClick={handleSelectVideo}>
            选择视频
          </Button>
        </div>
        {!!video.mediaUrl && (
          <>
            <div className="mt-20 mb-10">视频位置</div>
            <Dropdown
              selected={video.pcPosition}
              items={posItems}
              onChange={(posId) => {
                updateVideo({ key: 'pcPosition', value: posId });
                updateVideo({ key: 'mobilePosition', value: posId });
              }}
            />
            <Slider
              className="mt-10"
              min={0.5}
              max={2}
              value={video.scale ?? 1}
              valToString={(x) => toPercent(x, 0)}
              onChange={(val) => {
                updateVideo({ key: 'scale', value: val });
              }}
            >
              <Slider.Header title="缩放比例：" />
              <Slider.Bar step={0.01} />
              <Slider.Footer />
            </Slider>
            <FeatureToggle
              title="讲解一遍结束后不再讲解"
              isOn={!!video.stopAfterPlayOnce}
              onToggle={(bool) =>
                updateVideo({ key: 'stopAfterPlayOnce', value: toFlag(bool) })
              }
            />
            <Tip className="mt-10 text-orange">{TIP}</Tip>
          </>
        )}
      </>
    );
  };

  return (
    <Collapse title="AI讲解">
      {featureVideo && (
        <Dropdown
          className="mb-20"
          items={typeItems}
          selected={voiceType}
          onChange={handleTypeChange}
        />
      )}
      {voiceType === 'audio' && renderAudioSetting()}
      {voiceType === 'video' && renderVideoSetting()}
      <SidebarApplyTo
        title="把AI讲解应用到..."
        className="mt-20"
        onClick={handleApplyTo}
      />
    </Collapse>
  );
}
